import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import axios from 'axios';
import { useFeature } from '@/composables/use-feature';
import { useScenariosStore } from '@/stores/scenarios';
import JobAPI from '@enums/jobapi';

const POLL_INTERVAL_MS = 5000;

/**
 * Handles the polling variant: actively reconciles job status through the supplied
 * status URL on an interval, until the job reaches a terminal state.
 */
function usePollingJobStatus({
  getScenarioId,
  statusUrl,
  usePollingForUpdates,
  onTerminal,
  getPollingDetails,
}) {
  const details = ref([]);
  const progress = ref(0);
  let poller = null;

  async function fetchJobStatus() {
    const response = await axios.get(statusUrl(getScenarioId()));
    return response?.data?.data;
  }

  const stop = () => {
    if (!poller) return;
    clearInterval(poller);
    poller = null;
  };

  const poll = async () => {
    try {
      const statusData = await fetchJobStatus();
      const newDetails = getPollingDetails(statusData);
      if (newDetails != null) details.value = newDetails;
      if (statusData?.progressPercentage != null) progress.value = statusData.progressPercentage;
      if (statusData?.isTerminal) {
        stop();
        await onTerminal();
      }
    } catch {
      // swallow poll errors — server logs them; client retries on next tick
    }
  };

  const start = () => {
    if (!usePollingForUpdates.value || poller) return;
    details.value = [];
    progress.value = 0;
    poll();
    poller = setInterval(poll, POLL_INTERVAL_MS);
  };

  function resumeIfInProgress(currentStatus) {
    if (currentStatus && !JobAPI.isJobFinishedOrFailed(currentStatus)) start();
  }

  return { details, progress, start, stop, resumeIfInProgress };
}

/**
 * Handles the push variant: reads the scenario job state kept fresh by statusUpdate
 * SSEs, and makes a one-off reconcile request on mount for stale in-progress jobs.
 */
function usePushJobStatus({
  getScenarioId,
  statusUrl,
  usePollingForUpdates,
  currentJobStatus,
  onTerminal,
  getPushDetails,
}) {
  const details = computed(() => getPushDetails(currentJobStatus.value) ?? []);
  const progress = computed(() => currentJobStatus.value?.progress ?? 0);

  let terminalHandled = false;
  watch(
    () => currentJobStatus.value?.status,
    status => {
      if (usePollingForUpdates.value || !status) return;
      if (!JobAPI.isJobFinishedOrFailed(status)) {
        terminalHandled = false;
        return;
      }
      if (terminalHandled) return;
      terminalHandled = true;
      onTerminal();
    }
  );

  function reconcileOnceIfInProgress() {
    const status = currentJobStatus.value?.status;
    if (!status || JobAPI.isJobFinishedOrFailed(status)) return;
    // best-effort reconcile
    axios.get(statusUrl(getScenarioId())).catch(() => {});
  }

  return { details, progress, reconcileOnceIfInProgress };
}

/**
 * Provides shared service-backed job status handling, gated by the
 * `switching.productSwitchingServiceEnabled` feature flag.
 *
 * In polling mode it actively reconciles through the supplied status URL. In
 * push mode it reads the scenario job state kept fresh by statusUpdate SSEs,
 * and makes a one-off reconcile request on mount for stale in-progress jobs.
 */
export function useJobStatusTracker({
  getScenarioId,
  statusUrl,
  getJobStatus,
  onTerminal,
  getPollingDetails = () => [],
  getPushDetails = () => [],
}) {
  const { usePollingForUpdates, productSwitchingServiceEnabled } = useFeature();
  const scenariosStore = useScenariosStore();

  const currentJobStatus = computed(() => getJobStatus(scenariosStore.jobStatuses));

  const polling = usePollingJobStatus({
    getScenarioId,
    statusUrl,
    usePollingForUpdates,
    onTerminal,
    getPollingDetails,
  });
  const push = usePushJobStatus({
    getScenarioId,
    statusUrl,
    usePollingForUpdates,
    currentJobStatus,
    onTerminal,
    getPushDetails,
  });

  const serviceJobDetails = computed(() =>
    usePollingForUpdates.value ? polling.details.value : push.details.value
  );
  const serviceJobProgress = computed(() =>
    usePollingForUpdates.value ? polling.progress.value : push.progress.value
  );

  onMounted(() => {
    if (!productSwitchingServiceEnabled.value) return;
    if (usePollingForUpdates.value) {
      polling.resumeIfInProgress(currentJobStatus.value?.status);
    } else {
      push.reconcileOnceIfInProgress();
    }
  });

  onBeforeUnmount(polling.stop);

  return {
    serviceJobDetails,
    serviceJobProgress,
    start: () => {
      if (productSwitchingServiceEnabled.value) polling.start();
    },
    stop: polling.stop,
  };
}
